// Cloudflare Turnstile — bot protection for the public lead-capture forms.
//
// The site key below is a public identifier that is meant to ship in the client
// bundle (same category as the HubSpot form GUIDs and the GA4 measurement ID —
// see docs/ENV.md, "Not environment variables"). The matching *secret* key is
// used only by the Io `public-lead-capture` edge function to verify tokens
// server-side; it is not used by this app and must never live in this repo.

export const TURNSTILE_SITE_KEY = '0x4AAAAAAEjPQAvYss3V4_Jg';

const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

export interface TurnstileRenderOptions {
  sitekey: string;
  action?: string;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact' | 'flexible';
  callback?: (token: string) => void;
  'expired-callback'?: () => void;
  'timeout-callback'?: () => void;
  'error-callback'?: () => void;
}

export interface TurnstileApi {
  render: (
    container: HTMLElement | string,
    options: TurnstileRenderOptions
  ) => string | undefined;
  reset: (widgetId?: string) => void;
  remove: (widgetId?: string) => void;
  getResponse: (widgetId?: string) => string | undefined;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let loader: Promise<TurnstileApi> | null = null;

/**
 * Injects the Turnstile script on first use and resolves with the API once it
 * is ready. The script is loaded at most once per page; every widget shares
 * this promise. Rejects if the script cannot be loaded, in which case the next
 * call retries.
 */
export function loadTurnstile(): Promise<TurnstileApi> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('Turnstile requires a browser environment'));
  }

  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (loader) return loader;

  loader = new Promise<TurnstileApi>((resolve, reject) => {
    const onLoad = () => {
      if (window.turnstile) {
        resolve(window.turnstile);
      } else {
        loader = null;
        reject(new Error('Turnstile script loaded but the API is unavailable'));
      }
    };

    const onError = () => {
      loader = null;
      reject(new Error('Failed to load the Turnstile script'));
    };

    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', onLoad);
      existing.addEventListener('error', onError);
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);
    document.head.appendChild(script);
  });

  return loader;
}

/** Shown whenever a form is submitted before the widget has produced a token. */
export const TURNSTILE_REQUIRED_MESSAGE =
  'Please complete the "I\'m not a robot" verification before submitting.';
